/**
 * Rechenregeln des individuellen Zahlungsplans (Spec 2026-09-17, Nachtrag 01.10.2026, Abschnitt
 * "Individueller Editor"; Plan 2026-10-02, Aufgabe 3). Reine Funktionen ohne Zugriff auf Store, Netz
 * oder Komponenten - die Oberflaeche (`components/Drawer/PaymentTab/`) ruft sie nur auf.
 *
 * Kein Portierungsmodul: diese Datei hat kein Gegenstueck in `index.html` (der Bestand kennt weder
 * Herkunft noch Planeditor) und steht deshalb nicht in `frontend/scripts/gen-herkunft.ts`.
 *
 * Drei Zustaende, bestimmt allein aus `order_extras.payment_plan_source` und der Ratenzahl
 * (`planArtVon`): "keinPlan", "nachPlan" (Herkunft Vorlage/Lieferantenstandard) und "individuell".
 * `vorlageMangel`/`vorlageVerwendbar` spiegeln `backend/src/domaene/zahlungsvorlagen.ts` - wer dort
 * eine Regel aendert, muss sie hier nachziehen (einen Gleichstand-Test gibt es nicht; das Backend
 * prueft beim Uebernehmen selbst, siehe `vorlageMangel` dort).
 */
import { ANKER_LABEL, type Anker } from "./anker.ts";
import { normalizePaymentRow } from "./payments.ts";
import type { Order, OrderPayment } from "./types.ts";

export type PlanArt = "keinPlan" | "nachPlan" | "individuell";

/** Toleranz der 100-%-Pruefung, wie auf dem Server (`PUT payment-plan`). */
export const SUMMEN_TOLERANZ = 0.001;

const ANKER_WERTE: readonly string[] = Object.keys(ANKER_LABEL);

/**
 * Der Zustand der Bestellung. Herkunft Vorlage -> "nachPlan", `individuell` -> "individuell". Ohne
 * Herkunft (NULL, oder ein aelterer Server ohne die Spalte): ohne Raten "keinPlan"; mit Raten
 * "individuell" - das kommt nach der Migration 058 nicht mehr vor, waere aber sonst eine Bestellung,
 * die weder Plan noch Editor zeigt.
 */
export function planArtVon(bestellung: Order): PlanArt {
  const herkunft = (bestellung as { paymentPlanSource?: unknown }).paymentPlanSource;
  if (herkunft === "lieferantenstandard" || herkunft === "freigegebene_vorlage") return "nachPlan";
  if (herkunft === "individuell") return "individuell";
  const raten = (bestellung.payments as unknown[] | undefined) ?? [];
  return raten.length > 0 ? "individuell" : "keinPlan";
}

/**
 * Anzeigebezeichnung nach Position (Spec, "Zwei Zustaende statt Zahlungsart"; E3): eine Rate
 * "Gesamtzahlung"; bei mehreren die erste "Anzahlung", die letzte "Restzahlung", dazwischen
 * "Zwischenzahlung". Die gespeicherte Spalte `label` geht nicht ein.
 */
export function ratenBezeichnung(index: number, anzahl: number): string {
  if (anzahl <= 1) return "Gesamtzahlung";
  if (index === 0) return "Anzahlung";
  if (index === anzahl - 1) return "Restzahlung";
  return "Zwischenzahlung";
}

/** Nummerierung 01 ... n. */
export function ratenNummer(index: number): string {
  return String(index + 1).padStart(2, "0");
}

// ================= Anteile =================

/** Ein lesbarer Anteil groesser 0 (Dezimalkomma erlaubt), sonst `null` - wie `prozentAlsZahl` im Backend. */
export function anteilAlsZahl(roh: unknown): number | null {
  const text = typeof roh === "number" ? String(roh) : typeof roh === "string" ? roh.trim().replace(",", ".") : "";
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  const zahl = Number(text);
  return Number.isFinite(zahl) && zahl > 0 ? zahl : null;
}

/** "Anteil fehlt": leer, nicht numerisch oder <= 0. */
export function anteilFehlt(roh: unknown): boolean {
  return anteilAlsZahl(roh) === null;
}

function runde(zahl: number, stellen: number): number {
  const faktor = 10 ** stellen;
  return Math.round(zahl * faktor) / faktor;
}

/** Deutsche Schreibweise ohne Tausenderpunkt: 33.33 -> "33,33". */
function prozentText(zahl: number): string {
  return String(runde(zahl, 3)).replace(".", ",");
}

export function istHundert(summe: number): boolean {
  return Math.abs(summe - 100) <= SUMMEN_TOLERANZ;
}

/** Summe der lesbaren Anteile; Raten ohne Anteil zaehlen 0. */
export function anteilsSumme(raten: readonly { pct?: unknown }[]): number {
  return raten.reduce((s, r) => s + (anteilAlsZahl(r.pct) ?? 0), 0);
}

export interface Summenzeile {
  summe: number;
  /** Exakt 100 % und jede Rate mit lesbarem Anteil. */
  ok: boolean;
  text: string;
  /** Raten ohne lesbaren Anteil (Anzahl). */
  ohneAnteil: number;
}

/** Die Zeile unter der Liste: "Summe 90 % - es fehlen 10 %". */
export function summenzeile(raten: readonly { pct?: unknown }[]): Summenzeile {
  const summe = anteilsSumme(raten);
  const ohneAnteil = raten.filter((r) => anteilFehlt(r.pct)).length;
  const hundert = istHundert(summe);
  let text: string;
  if (hundert) text = `Summe ${prozentText(100)} %`;
  else if (summe < 100) text = `Summe ${prozentText(summe)} % – es fehlen ${prozentText(100 - summe)} %`;
  else text = `Summe ${prozentText(summe)} % – ${prozentText(summe - 100)} % zu viel`;
  if (ohneAnteil > 0) text += ` · ${ohneAnteil === 1 ? "1 Rate ohne Anteil" : `${ohneAnteil} Raten ohne Anteil`}`;
  return { summe, ok: hundert && ohneAnteil === 0 && raten.length > 0, text, ohneAnteil };
}

// ================= Entwurf =================

/** Eine Rate im Editor. `id` ist `null` fuer eine noch nicht gespeicherte Rate. */
export interface EntwurfRate {
  /** Stabiler React-Schluessel; bei gespeicherten Raten die id. */
  schluessel: string;
  id: string | null;
  pct: string;
  amount: string;
  anchor: Anker | null;
  dueDays: string;
  /** Nur bei Handdatum (anchor null) die Eingabe; sonst das zuletzt gespeicherte, vom Server gerechnete Datum. */
  dueDate: string;
}

let zaehler = 0;
function neuerSchluessel(): string {
  zaehler += 1;
  return `neu-${zaehler}`;
}

function text(wert: unknown): string {
  return wert === undefined || wert === null ? "" : String(wert);
}

function alsAnker(wert: unknown): Anker | null {
  return typeof wert === "string" && ANKER_WERTE.includes(wert) ? (wert as Anker) : null;
}

/** Der Entwurf aus den gespeicherten (fuer die Anzeige aufbereiteten) Raten. */
export function entwurfAusRaten(raten: readonly OrderPayment[]): EntwurfRate[] {
  return raten.map((p, index) => ({
    schluessel: p.id ? String(p.id) : `lokal-${index}`,
    id: p.id ? String(p.id) : null,
    pct: text(p.pct),
    amount: text(p.amount),
    anchor: alsAnker(p.anchor),
    dueDays: text(p.dueDays),
    dueDate: text(p.due || (p as { dueDate?: unknown }).dueDate).slice(0, 10),
  }));
}

/**
 * Fingerabdruck der Planfelder der gespeicherten Raten. Aendert er sich, hat jemand anderes den Plan
 * geaendert - ein Entwurf darauf waere veraltet. Status, Dollarkurs und `rowVersion` gehen bewusst
 * NICHT ein: die aendern sich durch die eigene sofortige Bearbeitung, ohne den Entwurf zu entwerten.
 */
export function planSignatur(raten: readonly OrderPayment[]): string {
  return JSON.stringify(
    raten.map((p) => [
      p.id ?? null,
      text(p.pct),
      text(p.amount),
      alsAnker(p.anchor),
      text(p.dueDays),
      text(p.due || (p as { dueDate?: unknown }).dueDate).slice(0, 10),
    ]),
  );
}

/** Der Plan "Individuellen Plan anlegen": eine Rate, 100 %, Bestelldatum, 0 Tage. */
export function ersterEntwurf(): EntwurfRate[] {
  return [{ schluessel: neuerSchluessel(), id: null, pct: "100", amount: "", anchor: "order_date", dueDays: "0", dueDate: "" }];
}

/** Hat der Entwurf andere Planfelder als die gespeicherten Raten? */
export function entwurfGeaendert(entwurf: readonly EntwurfRate[], gespeichert: readonly OrderPayment[]): boolean {
  return JSON.stringify(entwurf.map(planFelderVon)) !== JSON.stringify(entwurfAusRaten(gespeichert).map(planFelderVon));
}

function planFelderVon(r: EntwurfRate): unknown[] {
  return [r.id, r.pct, r.amount, r.anchor, r.dueDays, r.dueDate];
}

export type Aenderbar = "pct" | "amount" | "anchor" | "dueDays" | "dueDate";

/** Setzt ein Planfeld. Ein von Hand eingetragenes Datum macht aus der Ankerrate ein Handdatum (R4). */
export function planFeldSetzen(
  entwurf: readonly EntwurfRate[],
  index: number,
  feld: Aenderbar,
  wert: string,
): EntwurfRate[] {
  return entwurf.map((r, i) => {
    if (i !== index) return r;
    if (feld === "anchor") return { ...r, anchor: alsAnker(wert) };
    if (feld === "dueDate") return { ...r, dueDate: wert, anchor: wert !== "" ? null : r.anchor };
    return { ...r, [feld]: wert };
  });
}

// ================= Hinzufuegen und Loeschen =================

/** Gibt den Status einer Rate des Entwurfs zurueck (neue Raten: "open"). */
export type StatusVon = (rate: EntwurfRate) => string;

export type Aenderung = { ok: true; raten: EntwurfRate[] } | { ok: false; grund: string };

/**
 * "Rate hinzufuegen" (Spec): Summe < 100 % -> die neue Rate bekommt den Rest. Summe = 100 % -> die
 * letzte OFFENE Rate wird geteilt (sie behaelt die Haelfte, auf zwei Nachkommastellen, den
 * Rundungsrest; die neue Rate bekommt die andere Haelfte). Bezahlte und fixierte Raten werden nie
 * geteilt; gibt es keine offene, ist Hinzufuegen gesperrt. Summe > 100 % -> gesperrt. Anker und Tage
 * stammen von der letzten Rate, Status offen, kein Dollarkurs.
 */
export function rateHinzufuegen(entwurf: readonly EntwurfRate[], statusVon: StatusVon): Aenderung {
  const letzte = entwurf[entwurf.length - 1];
  const summe = anteilsSumme(entwurf);
  const vorlage = (pct: string): EntwurfRate => ({
    schluessel: neuerSchluessel(),
    id: null,
    pct,
    amount: "",
    anchor: letzte ? letzte.anchor : "order_date",
    dueDays: letzte ? letzte.dueDays : "0",
    dueDate: "",
  });
  if (!istHundert(summe) && summe > 100) {
    return { ok: false, grund: "Die Summe liegt über 100 % – bitte zuerst die Anteile korrigieren." };
  }
  if (!istHundert(summe)) {
    const rest = runde(100 - summe, 3);
    if (rest <= 0) return { ok: false, grund: "Es ist kein Anteil mehr frei." };
    return { ok: true, raten: [...entwurf, vorlage(String(rest))] };
  }
  // Summe 100 %: die letzte offene Rate teilen.
  let ziel = -1;
  for (let i = entwurf.length - 1; i >= 0; i -= 1) {
    if (statusVon(entwurf[i]) === "open") {
      ziel = i;
      break;
    }
  }
  if (ziel < 0) {
    return { ok: false, grund: "Es gibt keine offene Rate, die sich teilen lässt – bezahlte und fixierte Raten bleiben unverändert." };
  }
  const anteil = anteilAlsZahl(entwurf[ziel].pct);
  if (anteil === null) return { ok: false, grund: "Der Anteil der letzten offenen Rate fehlt." };
  // Die neue Rate bekommt die abgerundete Haelfte auf zwei Nachkommastellen; ALLES Uebrige - auch ein
  // Rest unterhalb der Hundertstel (66,667) - bleibt bei der bestehenden Rate, die Summe bleibt exakt.
  const neu = Math.floor(anteil * 50) / 100;
  if (neu <= 0) return { ok: false, grund: "Der Anteil der letzten offenen Rate ist zu klein zum Teilen." };
  const bleibt = runde(anteil - neu, 6);
  const geteilt = entwurf.map((r, i) => (i === ziel ? { ...r, pct: String(bleibt) } : r));
  return { ok: true, raten: [...geteilt, vorlage(String(neu))] };
}

/** Warum die Rate nicht geloescht werden darf, sonst `null`: bezahlt/fixiert, neu ist immer loeschbar, letzte bleibt. */
export function loeschenGesperrtGrund(
  entwurf: readonly EntwurfRate[],
  index: number,
  statusVon: StatusVon,
): string | null {
  const rate = entwurf[index];
  if (!rate) return "Rate nicht gefunden.";
  if (entwurf.length <= 1) return "Mindestens eine Rate bleibt.";
  const status = statusVon(rate);
  if (status === "paid") return "Eine bezahlte Rate lässt sich nicht löschen.";
  if (status === "fixed") return "Eine fixierte Rate lässt sich nicht löschen.";
  return null;
}

export function rateEntfernen(
  entwurf: readonly EntwurfRate[],
  index: number,
  statusVon: StatusVon,
): Aenderung {
  const grund = loeschenGesperrtGrund(entwurf, index, statusVon);
  if (grund !== null) return { ok: false, grund };
  return { ok: true, raten: entwurf.filter((_, i) => i !== index) };
}

// ================= Speichern =================

/** Warum "Speichern" gesperrt ist, sonst `null` (Summe exakt 100 %, jede Rate mit Anteil, mindestens eine Rate). */
export function speichernGesperrtGrund(entwurf: readonly EntwurfRate[]): string | null {
  if (entwurf.length === 0) return "Mindestens eine Rate ist nötig.";
  const zeile = summenzeile(entwurf);
  if (zeile.ohneAnteil > 0) return "Mindestens eine Rate hat keinen Anteil.";
  if (!istHundert(zeile.summe)) return "Die Anteile müssen zusammen genau 100 % ergeben.";
  return null;
}

/** Eine Zeile des Koerpers von `PUT /api/orders/:id/payment-plan` (Plan, E10). */
export interface PlanRateEingabe {
  id?: string;
  rowVersion?: number;
  pct: number;
  amount: number | null;
  anchor: Anker | null;
  dueDays: number | null;
  dueDate: string | null;
}

function zahlOderNull(roh: string): number | null {
  const t = roh.trim().replace(",", ".");
  if (t === "") return null;
  const z = Number(t);
  return Number.isFinite(z) ? z : null;
}

/**
 * Der Koerper in der Reihenfolge des Entwurfs. Bestehende Raten tragen `id` und die ZULETZT BEKANNTE
 * `rowVersion` (`gespeichert`); neue Raten tragen keine von beiden.
 */
export function alsPlanRaten(
  entwurf: readonly EntwurfRate[],
  gespeichert: readonly OrderPayment[],
): PlanRateEingabe[] {
  const nachId = new Map(gespeichert.filter((p) => p.id).map((p) => [String(p.id), p] as const));
  return entwurf.map((r) => {
    const bekannt = r.id !== null ? nachId.get(r.id) : undefined;
    const version = bekannt !== undefined ? Number(bekannt.rowVersion) : NaN;
    return {
      ...(r.id !== null ? { id: r.id } : {}),
      ...(r.id !== null && Number.isInteger(version) ? { rowVersion: version } : {}),
      pct: anteilAlsZahl(r.pct) ?? 0,
      amount: zahlOderNull(r.amount),
      anchor: r.anchor,
      dueDays: zahlOderNull(r.dueDays),
      dueDate: r.dueDate === "" ? null : r.dueDate,
    };
  });
}

/** "In Raten umwandeln" (E9): die aus dem Freitext abgeleiteten Raten als Entwurf, ungespeichert. */
export function entwurfAusAbgeleitetenRaten(raten: readonly OrderPayment[]): EntwurfRate[] {
  return raten.map((p) => ({
    schluessel: neuerSchluessel(),
    id: null,
    pct: text(p.pct),
    amount: text(p.amount),
    anchor: alsAnker(p.anchor),
    dueDays: text(p.dueDays),
    dueDate: text(p.due).slice(0, 10),
  }));
}

// ================= Vorlage verwendbar (Spiegel des Backends) =================

/** Wie `prozentAlsZahl` in `backend/src/domaene/zahlungsvorlagen.ts` (ohne die Pruefung auf > 0). */
function prozentAlsZahl(roh: unknown): number | null {
  const t = typeof roh === "number" ? String(roh) : typeof roh === "string" ? roh.trim().replace(",", ".") : "";
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  const zahl = Number(t);
  return Number.isFinite(zahl) ? zahl : null;
}

/**
 * Warum eine Vorlage nicht verwendbar ist, sonst `null`. Spiegel von `vorlageMangel`
 * (`backend/src/domaene/zahlungsvorlagen.ts`): rows nicht leer, jeder pct lesbar und > 0, Summe 100
 * (Toleranz 0,001), jeder anchor gesetzt und im Ankerkatalog. Der Server prueft dasselbe beim
 * Uebernehmen; die Oberflaeche sperrt die Auswahl nur vorab und nennt den Grund.
 */
export function vorlageMangel(rows: unknown): string | null {
  if (!Array.isArray(rows) || rows.length === 0) return "keine Zeilen";
  let summe = 0;
  for (const [index, zeile] of rows.entries()) {
    const z = (typeof zeile === "object" && zeile !== null ? zeile : {}) as { pct?: unknown; anchor?: unknown };
    const prozent = prozentAlsZahl(z.pct);
    if (prozent === null || prozent <= 0) return `Zeile ${index + 1}: kein gültiger Anteil`;
    summe += prozent;
    if (typeof z.anchor !== "string" || !ANKER_WERTE.includes(z.anchor)) {
      return `Zeile ${index + 1}: kein gültiger Anker`;
    }
  }
  if (Math.abs(summe - 100) > SUMMEN_TOLERANZ) return `Anteile ergeben ${Math.round(summe * 1000) / 1000} % statt 100 %`;
  return null;
}

export function vorlageVerwendbar(rows: unknown): boolean {
  return vorlageMangel(rows) === null;
}

// ================= Anzeige =================

/**
 * Die gespeicherten Raten fuer die ANZEIGE: jede Zeile normalisiert (Status, `paid`, leere Felder),
 * aber NICHTS erfunden. Ersetzt in der Bedienung `normalizeOrderPayments` (`lib/payments.ts`), das fuer
 * Split-Bestellungen zwei Standardraten mit leerem Anteil und bei null Raten die Phantomrate
 * "Gesamtzahlung 100" anlegte - die Quelle der Raten ohne Anteil (Spec, "Keine Rate ohne Anteil").
 * Ohne Raten bleibt die Liste leer (Zustand "Kein Zahlungsplan").
 */
export function ratenFuerAnzeige(raten: readonly OrderPayment[]): OrderPayment[] {
  return raten.map((p, index) => normalizePaymentRow({ ...p }, ratenBezeichnung(index, raten.length)));
}

/**
 * Haben zwei Entwuerfe denselben Inhalt, unabhaengig von Ids und Schluesseln? Das Datum zaehlt nur bei
 * Handdatum (bei einer Ankerrate rechnet der Server es neu). Damit erkennt der Editor, dass ein
 * geaenderter gespeicherter Stand das Ergebnis des EIGENEN Speicherns ist und keine fremde Aenderung.
 */
export function entwurfInhaltGleich(a: readonly EntwurfRate[], b: readonly EntwurfRate[]): boolean {
  const form = (r: EntwurfRate): unknown[] => [
    anteilAlsZahl(r.pct) ?? r.pct,
    zahlOderNull(r.amount),
    r.anchor,
    zahlOderNull(r.dueDays) ?? 0,
    r.anchor === null ? r.dueDate : "",
  ];
  return JSON.stringify(a.map(form)) === JSON.stringify(b.map(form));
}
